<script lang="ts">
	import type { ButtonProps } from '$lib/components/ui/button/button.svelte';
	import type { HTMLButtonAttributes } from 'svelte/elements';
	import type { Snippet } from 'svelte';

	import Button from '$lib/components/ui/button/button.svelte';
	import FaIcon from './FaIcon.svelte';

	import { Loc } from '$lib/tools/locale.svelte';
	import { utils } from '../utils.svelte';
	import { fly } from 'svelte/transition';
	import { toast } from '../toast';
	import { cn } from '$lib/utils';

	type OnConfirmFn = (...args: Parameters<NonNullable<HTMLButtonAttributes['onclick']>>) => Promise<boolean>;

	let {
		onclick,
		children,
		disabled,
		toasts,
		safety_clicks,
		onsuccess,
		class: className,
		title,
		onfail,
		disable_spinner,
		ref = $bindable(null),
		...restProps
	}: {
		onclick?: OnConfirmFn;
		disable_spinner?: boolean;
		toasts?: { success?: string; error?: string };
		onsuccess?: () => void;
		onfail?: () => void;
		safety_clicks?: boolean | number;
		ref?: HTMLButtonElement | null;
		children?: Snippet;
	} & HTMLButtonAttributes &
		ButtonProps = $props();

	let is_loading = $state(false);
	let is_button_size_set = false;

	const safety_clicks_handler = utils.create_safety_lock({
		onsuccess: handle_click,
		safety_clicks_fn: () => (safety_clicks === true ? 4 : typeof safety_clicks === 'number' ? safety_clicks : 0)
	});

	async function handle_click(e: any) {
		try {
			const element = e.target as HTMLButtonElement;
			if (!is_button_size_set) {
				element.style.width = `${element.clientWidth}px`;
				element.style.height = `${element.clientHeight}px`;
				is_button_size_set = true;
			}

			is_loading = true;
			const success = await onclick?.(e);

			if (!success) {
				if (onfail) onfail();
				if (toasts?.error) toast.error(toasts.error);
				return false;
			}

			if (onsuccess) onsuccess();
			if (toasts?.success) toast.success(toasts.success);
			return true;
		} catch (error) {
			console.error(error);
			return false;
		} finally {
			is_loading = false;
		}
	}
</script>

<Button
	class={cn(safety_clicks_handler.is_locked ? 'opacity-30 cursor-not-allowed relative' : '', className)}
	bind:ref
	{...restProps}
	disabled={disabled || is_loading}
	onclick={safety_clicks_handler.onclick}
>
	{#if is_loading && !disable_spinner}
		<FaIcon name="faSpinner" spin class="h-3.5 w-3.5" />
	{:else if title}
		{Loc(title)}
	{:else if children}
		{@render children()}
	{/if}
	{#if safety_clicks_handler.is_locked && safety_clicks_handler.unlock_progress > 0}
		<span
			transition:fly={{ y: 10, duration: 200 }}
			class="absolute -top-1 -right-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-xs font-bold text-destructive-foreground shadow-md pointer-events-none"
		>
			{safety_clicks_handler.clicks_remaining}
		</span>
	{/if}
</Button>
